import React, { createContext, useState, useEffect, useContext, ReactNode } from 'react';
import ApiService from 'src/services/ApiService';
import { Material, WorkOrder } from '@twinsketch/topika-model';
import { MixingFormulationWithMaterials } from '@twinsketch/topika-model';
import { useUser } from 'src/auth/UserContext';
import { isTokenValid } from 'src/auth/tokenUtils';

interface AppDataContextType {
  materials: Material[];
  formulations: MixingFormulationWithMaterials[];
  workOrders: WorkOrder[];
  loading: boolean;
  setMaterials: React.Dispatch<React.SetStateAction<Material[]>>;
  setFormulations: React.Dispatch<React.SetStateAction<MixingFormulationWithMaterials[]>>;
  setWorkOrders: React.Dispatch<React.SetStateAction<WorkOrder[]>>;
}

const AppDataContext = createContext<AppDataContextType | undefined>(undefined);

export const useAppData = () => {
  const context = useContext(AppDataContext);
  if (!context) {
    throw new Error('useAppData must be used within an AppDataProvider');
  }
  return context;
};

export const AppDataProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [materials, setMaterials] = useState<Material[]>([]);
  const [formulations, setFormulations] = useState<MixingFormulationWithMaterials[]>([]);
  const [workOrders, setWorkOrders] = useState<WorkOrder[]>([]);

  const [loading, setLoading] = useState<boolean>(true);
  const { platformUser } = useUser();
  const isValid = isTokenValid();

  useEffect(() => {
    const fetchData = async () => {
      if (isValid) {
        try {
          const [materialsResponse, formulationsData, workOrdersData] = await Promise.all([
            ApiService.fetchMaterials(),
            ApiService.fetchFormulations(),
            ApiService.fetchWorkOrders(),
          ]);
          setMaterials(materialsResponse as Material[]);
          setFormulations(formulationsData as MixingFormulationWithMaterials[]);
          setWorkOrders(workOrdersData as WorkOrder[]);
        } catch (error) {
          console.error('Error fetching data:', error);
        } finally {
          setLoading(false);
        }
      }
    };
    fetchData();
  }, [platformUser]);

  return (
    <AppDataContext.Provider
      value={{
        materials,
        formulations,
        workOrders,
        setMaterials,
        setFormulations,
        setWorkOrders,
        loading,
      }}
    >
      {children}
    </AppDataContext.Provider>
  );
};
